import R from 'ramda';
import gql from 'graphql-tag';
import h from 'react-hyperscript';
import hh from 'hyperscript-helpers';
import moment from 'moment';
import { Component } from 'react';
import { fromGlobalId } from 'graphql-relay';
import { graphql } from 'react-apollo';

import Button from '../Button';
import Card from '../Card';
import Container from '../Container';
import Footer from '../Footer';
import Grid from '../Grid';
import Header from '../Header';
import HeroText from '../HeroText';
import Schedule from '../Schedule';
import Search from '../Search';
import SectionHeader from '../SectionHeader';
import TeachersGrid from '../TeachersGrid';

import jamesImg from '../images/student-james-clarke.jpg';
import paulImg from '../images/teacher-paul-fitzgerald.jpg';
import hassanImg from '../images/teacher-hassan-mir.jpg';

import styles from './javascript.css';
import ImageBesideText from '../ImageBesideText';

const { div, p, br, a } = hh(h);


// 
// Data for the course ui
// 
const detailCards = [
  {
    headerText: 'You’ll create',
    bodyText: 'A dynamic website with JavaScript functionality',
    theme: 'jsCourse'
  },
  {
    headerText: 'You’ll learn',
    bodyList: [
      'Programming principles.',
      'JavaScript language.',
      'How interactive websites and web-apps are developed.'
    ],
    theme: 'jsCourse'
  },
  {
    headerText: 'You’ll leave with',
    bodyText: 'An understanding of programming principles, JavaScript and an understanding of how to approach programming challenges.',
    theme: 'jsCourse'
  }
];

const schedules = [
  {
    title: 'Saturday',
    schedule: [
      {
        startTime: '10:00',
        endTime: '13:00',
        bodyText: 'First we’ll cover a brief introduction/refresher of HTML, CSS & the web. Then we will introduce some programming principles.',
        theme: 'jsCourse'
      },
      {
        startTime: '13:30',
        endTime: '14:00',
        bodyText: 'Lunch - a chance to relax, recharge and ask any questions you have from the morning’s class.',
        theme: 'jsCourse'
      },
      {
        startTime: '14:00',
        endTime: '17:00',
        bodyText: 'Introduction to JavaScript - we’ll pack a lot in: variables, types, arrays, conditionals to name a few concepts. By the end of the day you will have made a huge amount of progress on JavaScript - a language which is essential to almost every  website online.',
        theme: 'jsCourse'
      }
    ]
  },
  {
    title: 'Sunday',
    schedule: [
      {
        startTime: '10:00',
        endTime: '13:00',
        bodyText: 'We’ll introduce you to JavaScript frameworks like Node.js and Express.js that give you the structure you need to build your own dynamic websites with meaningful interactions.',
        theme: 'jsCourse'
      },
      {
        startTime: '13:00',
        endTime: '14:00',
        bodyText: 'Lunch - another chance to relax, recharge and ask any questions you have from the morning’s class.',
        theme: 'jsCourse'
      },
      {
        startTime: '14:00',
        endTime: '17:00',
        bodyText: 'The last section of the course is focused on applying the lessons you’ve learned in previous sections  to real world applications. It’s also a chance to make sure you fully understand the concepts you’ve learned.',
        theme: 'jsCourse'
      }
    ]
  }
];


// 
// Sets of elements
// 
const StudentsSection = [
  h(SectionHeader, 'What past students said after completing the course'),
  h(ImageBesideText, { image: jamesImg }, [
    p({ className: styles.testimonialBody }, '"Code at Uni’s JavaScript course was the perfect first step on my path to becoming a junior web developer. It was an intense weekend but our instructors were great and helped me cram in weeks of individual learning into two days"'),
    a({ className: styles.testimonialFrom, href: 'https://www.linkedin.com/in/jamesraymondclarke/' }, 'James Clarke')
  ]),
  h(ImageBesideText, {}, [
    p({ className: styles.testimonialBody }, '"It was an intense bootcamp, where I learnt a great deal. It was almost like a few weeks worth of programming within 12 hours. The tasks we completed were very relevant and useful - we did not focus on theoretical aspects, more practical uses."'),
    div({ className: styles.testimonialFrom }, 'BSc Accounting & Finance student, University of Birmingham')
  ])
];


const teachers = [
  {
      name: 'Paul Fitzgerald',
      bio: 'Paul began his career as a lawyer for Skype but after witnessing first hand the power of technology, Paul teach himself to code. Since then, Paul’s development career has seen him work for brands like Karen Millen and Sony. Paul’s broad commercial experience means he can break every topic down into its simplest form and constantly help students relate their classroom work back to real world problems.',
      image: paulImg,
      role: 'Software Engineer, Dow Jones'
  },
  {
      name: 'Hassan Mir',
      bio: 'Hassan is a software engineer with a passion for teaching which first led him to teach with General Assembly in London and now with Code at Uni. Hassan has a background in Mathematics and is a lead developer at one of London’s most disruptive startups in the insurance space. Hassan loves sharing the power of coding with his students and talking about real world applications.',
      image: hassanImg,
      role: 'Software Engineer, Claimable'
  }
];



class JavaScriptCourse extends Component {
  constructor(props) {
    super(props);
    this.state = {
      query: this.props.query
    };
  }
  
  render() {
    const {
      isOpen,
      course,
      courses
    } = this.props;
    
    const openApplyButton = (course) => (
      div({className: styles.applyButtonContainer},
        h(Button, {href: `/course/${fromGlobalId(course.id).id}`, arrow: true, arrowPosition: 'front', theme: 'white', size: 'big'}, 'Apply now')
      )
    );
    
    const closedApplyButton = (course) => (
      div(
        {
          className: styles.applyButtonContainer
        },
        [
          p({ className: styles.applyHelp }, [
            'Hang tight! Applications for the course at ',
            br(),
            `${course.university.name} for ${moment(new Date(course.startDate)).format('MMM Do')}—${moment(new Date(course.endDate)).format('MMM Do')} `,
            br(),
            'will open soon.'])
        ]
      )
    );
    
    const JsSearch = graphql((gql`
    query{
      courses(where: { published: true, type: "js" }) {
        id
        name
        description
        level
        startDate
        endDate
        state
        tbd
        type
        
        university {
          name
          shortName
        }
      }
    }
    `))(Search);
    
    const applyButton = (isOpen, course) => {
      if (R.isNil(course)) {
        throw new Error('`course` cannot be `null`');
      }
      
      return isOpen ? openApplyButton(course) : closedApplyButton(course);
    };
    
    const cta = ({isOpen, course, courses}) => {
      if (R.isNil(course)) {
        return div({className: styles.searchContainer}, [
          h(JsSearch, {placeholder: 'e.g. University College London', helpText: 'Search for your uni to find an JavaScript course', query: this.state.query})]);
      } else {
        return applyButton(isOpen, course);
      }
    };
    
    return (
      div({className: styles.container}, [
        h(Header),
        h(Container, {theme: 'js', size: 'big'}, [
          div({className: styles.heroContainer}, [
            h(HeroText, {subText: 'Learn the programming language of the internet'}, 'JavaScript'),
            cta({isOpen, course, courses})
          ])
        ]),
        h(Container, {size: 'big', theme: 'neutral'}, [
          h(SectionHeader, 'Details'),
          h(Grid, R.map((props) => h(Card, props), detailCards))]),
          h(Container, {size: 'big'}, [
            h(SectionHeader, {theme: 'two'}, 'Schedule'),
            h(Schedule, {schedules})]),
            h(Container, {theme: 'neutral', size: 'big'}, StudentsSection),
            h(Container, { alignItems: 'flexStart' }, [
              h(SectionHeader, 'Your instructors'), 
              h(TeachersGrid, { teachers })
            ]),
            h(Container, [
              h(Footer)])
      ])
    );
  };
};
          
export default JavaScriptCourse;
              


/** WEBPACK FOOTER **
 ** ./src/JavascriptCourse/index.js
 **/